
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Rocket from 'plaid-threads/Icons/Rocket';
import Video from 'plaid-threads/Icons/Video';
import Libraries from 'plaid-threads/Icons/Libraries';
import Actions from '../../../components/docs/Actions';
export const metadata = {
  layout: 'guide',
  description: 'Learn about Payroll Income features and implementation',
  section: 'Income',
  title: 'Payroll Income'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Header title='Payroll Income' subtitle={metadata.description} mdxType="Header" />
    <Actions.SmallWrapper miniTitle='Get started with Payroll Income'>
  <Actions.SmallCard icon={<Libraries mdxType="Libraries" />} title='API Reference' link='/docs/api/products/income/' />
  <Actions.SmallCard icon={<Rocket mdxType="Rocket" />} title='Quickstart' link='https://github.com/plaid/income-sample' />
    </Actions.SmallWrapper>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Payroll Income (US only) allows you to instantly verify employment details and gross income information, including the information available on a pay stub, from a user-connected payroll account. Payroll Income supports approximately 80% of the US workforce, including gig income workers.`}</p>
    <YouTubeEmbed videoId='Hc-MpibSxJE' alignment='full' title='Plaid Income in 3 minutes' caption='Prefer to learn by watching? Get an overview of how Income works in just 3 minutes!' mdxType="YouTubeEmbed" />
    <h4 {...{
      "id": "integration-process"
    }}>{`Integration process`}</h4>
    <Callout mdxType="Callout">
      <p>{`The flow below describes the latest integration pattern for Plaid Income. If you are an existing customer who integrated before August 17, 2026, your API calls have a slightly different interface. For details on the differences, see `}<a parentName="p" {...{
          "href": "/docs/api/users/user-apis/"
        }}>{`New User APIs`}</a>{`.`}</p>
    </Callout>
    <ol>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/user/create`}</inlineCode>{` to create a `}<inlineCode parentName="li">{`user_id`}</inlineCode>{` that will represent the end user interacting with your application. This step will only need to be done once per end user. If you are using multiple Income types, do not repeat this step when switching to a different Income type.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{`. In addition to the required parameters, you will need to provide the following:`}
        <ul parentName="li">
          <li parentName="ul">{`For `}<inlineCode parentName="li">{`user_id`}</inlineCode>{`, provide the `}<inlineCode parentName="li">{`user_id`}</inlineCode>{` from `}<inlineCode parentName="li">{`/user/create`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`For `}<inlineCode parentName="li">{`products`}</inlineCode>{`, use `}<inlineCode parentName="li">{`["income_verification"]`}</inlineCode>{`. Payroll Income cannot be used in the same Link session as any other Plaid products, except for Document Income (which may be offered as a fallback when a payroll provider is not supported).`}</li>
          <li parentName="ul">{`For `}<inlineCode parentName="li">{`income_verification.income_source_types`}</inlineCode>{`, use `}<inlineCode parentName="li">{`payroll`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`(Optional) If you are only using Payroll Income and do not want customers to use Document Income, for `}<inlineCode parentName="li">{`income_verification.payroll_income.flow_types`}</inlineCode>{`, use `}<inlineCode parentName="li">{`["payroll_digital_income"]`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`Provide a `}<inlineCode parentName="li">{`webhook`}</inlineCode>{` URI with the endpoint where you will receive Plaid webhooks.`}</li>
        </ul>
      </li>
      <li parentName="ol">{`On the client side, create an instance of Link using the `}<inlineCode parentName="li">{`link_token`}</inlineCode>{` returned by `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{`; for more details, see `}<a parentName="li" {...{
          "href": "/docs/link"
        }}>{`Link`}</a>{`.`}</li>
      <li parentName="ol">{`Open Link in your web or mobile client and listen to the `}<inlineCode parentName="li">{`onSuccess`}</inlineCode>{` and `}<inlineCode parentName="li">{`onExit`}</inlineCode>{` callbacks, which will fire once the user has finished or exited the Link session.`}</li>
      <li parentName="ol">{`Listen for the `}<a parentName="li" {...{
          "href": "/docs/api/products/income/#user_income_verification"
        }}><inlineCode parentName="a">{`INCOME: USER_INCOME_VERIFICATION`}</inlineCode></a>{` webhook, which will fire within a few seconds, indicating that the Income data is ready.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/credit/payroll_income/get`}</inlineCode>{` for income data, and/or `}<inlineCode parentName="li">{`/credit/employment/get`}</inlineCode>{` for employment details.`}</li>
    </ol>
    <h4 {...{
      "id": "payroll-income-refresh"
    }}>{`Payroll Income Refresh`}</h4>
    <Callout mdxType="Callout">
      <p>{`To request access to Payroll Income Refresh, contact your account manager.`}</p>
    </Callout>
    <p>{`On-demand Payroll Income Refresh allows you to request updated information from a previously connected payroll account.`}</p>
    <p>{`To trigger a refresh, call `}<inlineCode parentName="p">{`/credit/payroll_income/refresh`}</inlineCode>{` and specify the `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` to refresh.`}</p>
    <p>{`If the refresh is successful, you will receive a `}<a parentName="p" {...{
        "href": "/docs/api/products/income/#user_income_verification"
      }}><inlineCode parentName="a">{`USER_INCOME_VERIFICATION`}</inlineCode></a>{` webhook. The next time you call `}<inlineCode parentName="p">{`/credit/payroll_income/get`}</inlineCode>{`, you will receive updated data.`}</p>
    <p>{`If the refresh was not successful, you will receive a `}<a parentName="p" {...{
        "href": "/docs/api/products/income/#user_income_verification_refresh_reconnect_needed"
      }}><inlineCode parentName="a">{`USER_INCOME_VERIFICATION_REFRESH_RECONNECT_NEEDED`}</inlineCode></a>{` webhook. To resolve this failure state, send the user through `}<a parentName="p" {...{
        "href": "/docs/link/update-mode"
      }}>{`update mode`}</a>{`. The refresh attempt will automatically be retried once the user has completed update mode, and a `}<a parentName="p" {...{
        "href": "/docs/api/products/income/#user_income_verification"
      }}><inlineCode parentName="a">{`USER_INCOME_VERIFICATION`}</inlineCode></a>{` webhook will be sent if the retry is successful.`}</p>
    <h4 {...{
      "id": "download-original-documents"
    }}>{`Download original documents`}</h4>
    <p>{`To download PDF versions of the original payroll documents that were parsed to obtain payroll data, use the `}<inlineCode parentName="p">{`document_metadata.download_url`}</inlineCode>{` returned by `}<inlineCode parentName="p">{`/credit/payroll_income/get`}</inlineCode>{`. Not all integrations will return a `}<inlineCode parentName="p">{`download_url`}</inlineCode>{`.`}</p>
    <p>{`In cases where an original source cannot be obtained, Plaid can optionally generate a PDF pay stub based on the data obtained. To enable Plaid-generated PDF pay stubs, contact your account manager. `}<a parentName="p" {...{
        "href": "https://plaid.com/documents/plaid-generated-mock-paystub.pdf"
      }}>{`View an example generated pay stub`}</a>{`.`}</p>
    <h4 {...{
      "id": "employment-data"
    }}>{`Employment data`}</h4>
    <p>{`Plaid provides employment data via the `}<inlineCode parentName="p">{`/credit/employment/get`}</inlineCode>{` endpoint. If you are on a Pay-as-you-go or Growth plan and want to use this endpoint, contact support to request access.`}</p>
    <h4 {...{
      "id": "testing-payroll-income"
    }}>{`Testing Payroll Income`}</h4>
    <p>{`You can test Payroll Income in Sandbox using Link. For best results, select a payroll provider that only requests a username, password, and/or SSN. (Good examples are Paycom, Paychex, or Workday.) Use `}<inlineCode parentName="p">{`user_good`}</inlineCode>{` as the username, `}<inlineCode parentName="p">{`pass_good`}</inlineCode>{` as the password, and `}<inlineCode parentName="p">{`1234`}</inlineCode>{` as the last 4 digits of your SSN if prompted.`}</p>
    <p>{`You can also see an example Link flow for Payroll Income using the `}<a parentName="p" {...{
        "href": "https://plaid.com/link-demo/"
      }}>{`Plaid Link demo site`}</a>{`: select "Payroll and Document Income" from the drop-down. For the demo site, use `}<inlineCode parentName="p">{`test-good`}</inlineCode>{` as the username, `}<inlineCode parentName="p">{`test`}</inlineCode>{` as the password, and `}<inlineCode parentName="p">{`1234`}</inlineCode>{` as the last 4 digits of your SSN.`}</p>
    <p><inlineCode parentName="p">{`/credit/payroll_income/get`}</inlineCode>{` can optionally be tested in Sandbox without using Link. Call `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{`, pass the returned `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` to `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{` (use `}<inlineCode parentName="p">{`ins_135842`}</inlineCode>{` when creating a public token for Payroll Income in Sandbox), and then call `}<inlineCode parentName="p">{`/credit/payroll_income/get`}</inlineCode>{`. The output of `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{` will not be used, but calling it initializes the user for testing.`}</p>
    <p>{`Payroll Income does not currently support the use of custom Sandbox data or provider-specific scenarios; only the default `}<inlineCode parentName="p">{`user_good`}</inlineCode>{` user is compatible with Sandbox Payroll Income.`}</p>
    <h4 {...{
      "id": "payroll-income-pricing"
    }}>{`Payroll Income pricing`}</h4>
    <p>{`Payroll Income is billed on a `}<a parentName="p" {...{
        "href": "/docs/account/billing/#one-time-fee"
      }}>{`one-time fee model`}</a>{`. Payroll Income Refresh is billed on a `}<a parentName="p" {...{
        "href": "/docs/account/billing/#per-request-flat-fee"
      }}>{`per-request fee model`}</a>{`. To view the exact pricing you may be eligible for, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/overview/production"
      }}>{`apply for Production access`}</a>{` or `}<a parentName="p" {...{
        "href": "https://plaid.com/contact/"
      }}>{`contact sales`}</a>{`. For more details about pricing and billing models, see `}<a parentName="p" {...{
        "href": "/docs/account/billing"
      }}>{`Plaid billing`}</a>{`.`}</p>
    <h4 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h4>
    <p>{`If you're ready to launch to Production, see the `}<a parentName="p" {...{
        "href": "/docs/launch-checklist/"
      }}>{`Launch checklist`}</a>{`.`}</p>
    <Actions.Wrapper>
  <Actions.Card icon={<Rocket mdxType="Rocket" />} title='Launch Center' subtitle='See next steps to launch in Production' link='https://dashboard.plaid.com/developers/launch-center' linkText='Launch' />
    </Actions.Wrapper>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "integration-process",
        level: 4,
        title: "Integration process",
      },
{
        id: "payroll-income-refresh",
        level: 4,
        title: "Payroll Income Refresh",
      },
{
        id: "download-original-documents",
        level: 4,
        title: "Download original documents",
      },
{
        id: "employment-data",
        level: 4,
        title: "Employment data",
      },
{
        id: "testing-payroll-income",
        level: 4,
        title: "Testing Payroll Income",
      },
{
        id: "payroll-income-pricing",
        level: 4,
        title: "Payroll Income pricing",
      },
{
        id: "next-steps",
        level: 4,
        title: "Next steps",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
